import { useState, useEffect, useCallback } from 'react'
import {
  Radar, RefreshCw, Search, Shield, Cloud, Globe, Newspaper, Lightbulb, Activity,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const SEVERITY_STYLES: Record<string, { bg: string; text: string }> = {
  critical: { bg: 'bg-rose/10',    text: 'text-rose-light' },
  high:     { bg: 'bg-amber/10',   text: 'text-amber-light' },
  medium:   { bg: 'bg-blue/10',    text: 'text-blue-light' },
  low:      { bg: 'bg-emerald/10', text: 'text-emerald-light' },
  info:     { bg: 'bg-purple/10',  text: 'text-purple-light' },
}

const CATEGORY_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  cyber:      { icon: Shield,    color: 'text-rose-light',   label: 'Cyber' },
  cloud:      { icon: Cloud,     color: 'text-cyan-light',   label: 'Cloud' },
  innovation: { icon: Lightbulb, color: 'text-purple-light', label: 'Innovation' },
  world:      { icon: Globe,     color: 'text-amber-light',  label: 'World' },
  tech_news:  { icon: Newspaper, color: 'text-blue-light',   label: 'Tech News' },
}

const REGION_FLAGS: Record<string, string> = {
  US: '\u{1F1FA}\u{1F1F8}', EU: '\u{1F1EA}\u{1F1FA}', GB: '\u{1F1EC}\u{1F1E7}',
  CN: '\u{1F1E8}\u{1F1F3}', TW: '\u{1F1F9}\u{1F1FC}', JP: '\u{1F1EF}\u{1F1F5}',
  KR: '\u{1F1F0}\u{1F1F7}', AU: '\u{1F1E6}\u{1F1FA}', Global: '\u{1F310}',
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

function scoreLabel(score: number): { label: string; color: string } {
  if (score >= 9) return { label: 'CRITICAL', color: 'badge-red' }
  if (score >= 7) return { label: 'HIGH', color: 'badge-amber' }
  if (score >= 5) return { label: 'MEDIUM', color: 'badge-blue' }
  return { label: 'LOW', color: 'badge-green' }
}

export default function SignalsPage() {
  const [signals, setSignals] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/signals/feed', {
        params: {
          limit: 100,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          severity: severityFilter !== 'all' ? severityFilter : undefined,
        },
      })
      setSignals(data.signals || [])
      setTotalCount(data.total || 0)
    } finally {
      setLoading(false)
    }
  }, [categoryFilter, severityFilter])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 60_000); return () => clearInterval(i) }, [fetchData])

  const filtered = signals.filter(s => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      return (s.title || '').toLowerCase().includes(q) || (s.summary || '').toLowerCase().includes(q)
    }
    return true
  })

  // Count per category
  const categoryCounts = signals.reduce((acc: Record<string, number>, s) => {
    acc[s.category] = (acc[s.category] || 0) + 1
    return acc
  }, {})

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Radar size={24} className="text-amber" /> All Signals
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Unified intelligence from 28+ real data sources — {totalCount} signals live
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={() => setCategoryFilter('all')} className={clsx('badge text-xs cursor-pointer px-3 py-1.5 transition-colors', categoryFilter === 'all' ? 'badge-blue' : 'bg-surface-card text-text-muted hover:text-text-secondary border border-border')}>
          All ({totalCount})
        </button>
        {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
          <button type="button" key={key} onClick={() => setCategoryFilter(key)} className={clsx('badge text-xs cursor-pointer px-3 py-1.5 transition-colors', categoryFilter === key ? 'badge-blue' : 'bg-surface-card text-text-muted hover:text-text-secondary border border-border')}>
            <val.icon size={12} /> {val.label} ({categoryCounts[key] || 0})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search signals..." className="input pl-9 py-2 text-xs" />
        </div>
        <select title="Filter by severity" value={severityFilter} onChange={e => setSeverityFilter(e.target.value)} className="input w-auto py-2 text-xs">
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="info">Info</option>
        </select>
      </div>

      {loading && !signals.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Aggregating from 28+ live sources...</span>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((signal, idx) => {
          const cat = CATEGORY_CONFIG[signal.category] || { icon: Activity, color: 'text-text-muted', label: signal.category }
          const sev = SEVERITY_STYLES[signal.severity] || SEVERITY_STYLES.info
          const sc = scoreLabel(signal.score || 5)
          const CatIcon = cat.icon
          return (
            <div key={idx} className="card-hover p-4">
              <div className="flex items-start gap-3">
                <CatIcon size={18} className={clsx('mt-0.5 shrink-0', cat.color)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={clsx('badge text-[10px]', sev.bg, sev.text)}>{signal.severity}</span>
                    <span className={clsx('badge text-[10px]', sc.color)}>{sc.label}</span>
                    <span className="text-[10px] text-text-muted">{REGION_FLAGS[signal.region] || ''} {signal.region}</span>
                    <span className="text-[10px] text-text-muted ml-auto">{signal.source} &middot; {relativeTime(signal.timestamp)}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-text-primary leading-snug">
                    {signal.url ? <a href={signal.url} target="_blank" rel="noopener" className="hover:underline">{signal.title}</a> : signal.title}
                  </h3>
                  <p className="text-xs text-text-secondary mt-1 leading-relaxed">{signal.summary}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {(signal.tags || []).map((tag: string, i: number) => (
                      <span key={i} className="text-[10px] text-text-muted bg-surface-hover px-1.5 py-0.5 rounded">{tag}</span>
                    ))}
                    {signal.score > 0 && (
                      <span className="text-[10px] text-text-muted ml-auto">
                        Score: <span className="font-mono font-bold">{Number(signal.score).toFixed(1)}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
        {!loading && filtered.length === 0 && <p className="text-sm text-text-muted text-center py-8">No signals match filters</p>}
      </div>
    </div>
  )
}
